import { Link } from '@inertiajs/react'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type CrmFundraisingDonation from '@/types/serializers/CrmFundraisingDonation'
import type CrmFundraisingRecurringDonation from '@/types/serializers/CrmFundraisingRecurringDonation'
import {
  DONATION_STATUS_LABELS,
  RECURRING_STATUS_LABELS,
  FREQUENCY_LABELS,
  STATUS_BADGE_VARIANT,
} from '@/lib/fundraising-labels'

interface LinkedDonationsTableProps {
  donations: CrmFundraisingDonation[]
  type: 'donation' | 'recurring'
  recurringDonations?: CrmFundraisingRecurringDonation[]
}

function statusLabel(status: string, type: 'donation' | 'recurring'): string {
  const labels = type === 'donation' ? DONATION_STATUS_LABELS : RECURRING_STATUS_LABELS
  return labels[status] ?? status
}

export function LinkedDonationsTable({
  donations,
  type,
  recurringDonations = [],
}: LinkedDonationsTableProps) {
  if (type === 'recurring') {
    return <RecurringDonationsTable recurringDonations={recurringDonations} />
  }

  if (donations.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        Aucun don lie a cette opportunite.
      </p>
    )
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Donateur</TableHead>
            <TableHead>Montant</TableHead>
            <TableHead>Date</TableHead>
            <TableHead>Campagne</TableHead>
            <TableHead>Statut</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {donations.map((donation) => {
            const status = typeof donation.status === 'object' ? donation.status.value : donation.status
            const statusStr = String(status)
            return (
              <TableRow key={donation.id}>
                <TableCell>
                  <Link
                    href={`/fundraising/donations/${donation.id}`}
                    className="font-medium text-primary hover:underline"
                  >
                    {donation.donor_name}
                  </Link>
                </TableCell>
                <TableCell className="tabular-nums">{donation.amount_formatted}</TableCell>
                <TableCell>{new Date(donation.donation_date).toLocaleDateString('fr-FR')}</TableCell>
                <TableCell>{donation.campaign_name}</TableCell>
                <TableCell>
                  <Badge variant={STATUS_BADGE_VARIANT[statusStr] ?? 'outline'}>
                    {statusLabel(statusStr, 'donation')}
                  </Badge>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}

function RecurringDonationsTable({
  recurringDonations,
}: {
  recurringDonations: CrmFundraisingRecurringDonation[]
}) {
  if (recurringDonations.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        Aucun don recurrent lie a cette opportunite.
      </p>
    )
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Donateur</TableHead>
            <TableHead>Montant / echeance</TableHead>
            <TableHead>Frequence</TableHead>
            <TableHead>Date debut</TableHead>
            <TableHead>Statut</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {recurringDonations.map((rd) => {
            const status = typeof rd.status === 'object' ? rd.status.value : rd.status
            const statusStr = String(status)
            const frequency = typeof rd.frequency === 'object' ? rd.frequency.value : rd.frequency
            const frequencyStr = String(frequency)
            return (
              <TableRow key={rd.id}>
                <TableCell>
                  <Link
                    href={`/fundraising/recurring_donations/${rd.id}`}
                    className="font-medium text-primary hover:underline"
                  >
                    {rd.donor_name}
                  </Link>
                </TableCell>
                <TableCell className="tabular-nums">
                  {rd.amount_per_installment_formatted}
                </TableCell>
                <TableCell>{FREQUENCY_LABELS[frequencyStr] ?? frequencyStr}</TableCell>
                <TableCell>{new Date(rd.start_date).toLocaleDateString('fr-FR')}</TableCell>
                <TableCell>
                  <Badge variant={STATUS_BADGE_VARIANT[statusStr] ?? 'outline'}>
                    {statusLabel(statusStr, 'recurring')}
                  </Badge>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}
